import React from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { ExternalLink, MapPin, Monitor, CheckCircle, Phone } from 'lucide-react';

interface WidgetPreviewProps {
  structure: any;
  onGoToPassport?: (slug?: string) => void;
  onGoToPassport2?: (slug?: string) => void;
  onSwitchToShareTab?: () => void;
  selectedStructureSlug?: string | null;
  fetchedStructure?: any;
  widgetSize: 'compact' | 'standard' | 'detailed';
  colorTheme: 'blue' | 'brand' | 'orange';
  language: 'it' | 'en' | 'de' | 'fr';
}

const getWidgetStyles = (theme: string) => {
  switch (theme) {
    case 'orange':
      return {
        gradient: 'bg-gradient-to-br from-orange-500 to-orange-600',
        primary: 'text-orange-600',
        primaryBg: 'bg-orange-500',
        accent: 'bg-orange-100 text-orange-600',
        border: 'border-orange-600'
      };
    case 'brand':
      return {
        gradient: 'bg-gradient-travelin',
        primary: 'text-travelin-blue',
        primaryBg: 'bg-gradient-travelin',
        accent: 'bg-blue-100 text-travelin-blue',
        border: 'border-travelin-blue'
      };
    default: // blue
      return {
        gradient: 'bg-gradient-travelin',
        primary: 'text-travelin-blue',
        primaryBg: 'bg-gradient-travelin',
        accent: 'bg-blue-100 text-travelin-blue',
        border: 'border-travelin-blue'
      };
  }
};

const getWidgetText = (lang: string) => {
  const texts: any = {
    it: {
      title: 'Passaporto Accessibilità',
      digitalTitle: 'Passaporto Digitale di Accessibilità',
      available: 'Passaporto Accessibilità Disponibile',
      complete: "Informazioni complete sull'accessibilità",
      documentation: "Documentazione completa sull'accessibilità",
      description: "Documentazione completa dell'accessibilità disponibile. Questo passaporto fornisce informazioni dettagliate su caratteristiche, servizi e strutture di accessibilità per tutti i visitatori.",
      viewButton: 'Visualizza Passaporto',
      viewComplete: 'Visualizza Passaporto Completo',
      verified: 'Verificato',
      poweredBy: 'Offerto da Travelin',
      certified: 'Certificato da Travelin • Ultimo aggiornamento 2 giorni fa',
      contact: 'Contatta'
    },
    en: {
      title: 'Accessibility Passport',
      digitalTitle: 'Digital Accessibility Passport',
      available: 'Accessibility Passport Available',
      complete: 'Complete accessibility information',
      documentation: 'Complete accessibility documentation',
      description: 'Comprehensive accessibility documentation available. This passport provides detailed information about accessibility features, services, and facilities for all visitors.',
      viewButton: 'View Passport',
      viewComplete: 'View Complete Passport',
      verified: 'Verified',
      poweredBy: 'Powered by Travelin',
      certified: 'Certified by Travelin • Last updated 2 days ago',
      contact: 'Contact'
    },
    de: {
      title: 'Barrierefreiheits-Pass',
      digitalTitle: 'Digitaler Barrierefreiheits-Pass',
      available: 'Barrierefreiheits-Pass Verfügbar',
      complete: 'Vollständige Barrierefreiheitsinformationen',
      documentation: 'Vollständige Barrierefreiheitsdokumentation',
      description: 'Umfassende Barrierefreiheitsdokumentation verfügbar. Dieser Pass bietet detaillierte Informationen über Barrierefreiheitsmerkmale, Services und Einrichtungen für alle Besucher.',
      viewButton: 'Pass Anzeigen',
      viewComplete: 'Vollständigen Pass Anzeigen',
      verified: 'Verifiziert',
      poweredBy: 'Powered by Travelin',
      certified: 'Zertifiziert von Travelin • Zuletzt aktualisiert vor 2 Tagen',
      contact: 'Kontakt'
    },
    fr: {
      title: "Passeport d'Accessibilité",
      digitalTitle: "Passeport Numérique d'Accessibilité",
      available: "Passeport d'Accessibilité Disponible",
      complete: "Informations complètes sur l'accessibilité",
      documentation: "Documentation complète sur l'accessibilité",
      description: "Documentation complète sur l'accessibilité disponible. Ce passeport fournit des informations détaillées sur les caractéristiques, services et installations d'accessibilité pour tous les visiteurs.",
      viewButton: 'Voir le Passeport',
      viewComplete: 'Voir le Passeport Complet',
      verified: 'Vérifié',
      poweredBy: 'Propulsé par Travelin',
      certified: 'Certifié par Travelin • Dernière mise à jour il y a 2 jours',
      contact: 'Contact'
    }
  };
  return texts[lang] || texts.it;
};

const getWidgetDimensions = (size: string) => {
  switch (size) {
    case 'compact':
      return {
        containerClass: 'max-w-xs p-3',
        iconSize: 'w-8 h-8',
        titleSize: 'text-m',
        padding: 'p-3'
      };
    case 'detailed':
      return {
        containerClass: 'max-w-xl p-8',
        iconSize: 'w-16 h-16',
        titleSize: 'text-xxl',
        padding: 'p-8'
      };
    default: // standard
      return {
        containerClass: 'max-w-md p-5',
        iconSize: 'w-12 h-12',
        titleSize: 'text-xl',
        padding: 'p-5'
      };
  }
};

export function WidgetPreview({ structure, onGoToPassport, onGoToPassport2, onSwitchToShareTab, selectedStructureSlug, fetchedStructure, widgetSize, colorTheme, language }: WidgetPreviewProps) {
  // Helper function to get structure name prioritizing "NOME STRUTTURA" (6.1) from "Dati info azienda"
  const getStructureName = (struct: any) => {
    // Helper to extract value from either a string or an object with .value property
    const extractValue = (val: any) => {
      if (val === null || val === undefined) return '';
      if (typeof val === 'object' && val.value !== undefined) {
        return String(val.value);
      }
      return String(val);
    };

    // PRIORITIZE "NOME STRUTTURA" (6.1) from "Dati info azienda" module
    if (Array.isArray(struct?.modules) && struct.modules.length > 0) {
      const firstModule = struct.modules[0];
      if (firstModule?.content) {
        // Try both numbered key (6.1) and human-readable key (NOME STRUTTURA)
        let nomeStruttura = extractValue(firstModule.content["6.1"]);
        if (!nomeStruttura) {
          nomeStruttura = extractValue(firstModule.content["NOME STRUTTURA"]);
        }
        if (nomeStruttura && nomeStruttura.trim()) {
          return nomeStruttura;
        }
      }
    }
    
    // Fallback to standard accommodation_name
    return struct?.structure?.accommodation_data?.accommodation_name || struct?.name || '';
  };
  
  const styles = getWidgetStyles(colorTheme);
  const text = getWidgetText(language);
  const dimensions = getWidgetDimensions(widgetSize);
  const hasPassport = Boolean(
    structure?.structure ||
    structure?.modules ||
    structure?.public_infos ||
    structure?.name ||
    fetchedStructure
  );

  return (
    <div className="flex flex-col lg:flex-row lg:items-start gap-4">
      <Card className="flex-1">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Monitor className="w-5 h-5" />
            Anteprima
          </CardTitle>
          <CardDescription>Questa è l’anteprima di come il widget verrà visualizzato dai visitatori del tuo sito: vai nella tab "Condividi", copia il codice e incollalo nel tuo sito.</CardDescription>
          {hasPassport && (
            <div className="mt-2 flex items-center gap-2 text-ss text-muted-foreground">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Il Passaporto è pronto per essere condiviso.
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="border-2 border-dashed border-border p-6 rounded-lg bg-muted/20">
            <div className="space-y-6">
            {widgetSize === 'compact' && (
              <div className="space-y-4">
                <h4 className="text-s font-medium text-muted-foreground mb-3">Compatto (280x120px)</h4>
                <div className={`bg-white border border-gray-200 rounded-lg ${dimensions.padding} shadow-sm ${dimensions.containerClass}`}>
                  <div className="flex items-center gap-3">
                    <div className={`${dimensions.iconSize} rounded-lg ${styles.gradient} flex items-center justify-center`}>
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="mb-1">
                        <span className={`text-s font-bold ${styles.primary}`}>{text.title}</span>
                      </div>
                      <p className="text-ss text-gray-600 truncate">{getStructureName(structure)}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <img src="/travelin-logo.svg" alt="Travelin" className="h-3 w-auto" />
                      <span className={`text-ss ${styles.primary} font-medium`}>{text.verified}</span>
                    </div>
                    {/* <Button size="sm" variant="outline" className="h-6 text-ss px-2" onClick={() => onGoToPassport && onGoToPassport(selectedStructureSlug ?? fetchedStructure?.slug)}>
                      {text.viewButton}
                    </Button> */}
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-ss px-2"
                      onClick={() => {
                        const resolvedStruct = structure || fetchedStructure;
                        const resolvedId = (resolvedStruct as any)?.id || fetchedStructure?.id;
                        const resolvedKey = resolvedId || selectedStructureSlug || fetchedStructure?.slug;
                        onGoToPassport2 && onGoToPassport2(resolvedStruct || resolvedKey);
                      }}
                    >
                      Visualizza Passaporto
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {widgetSize === 'standard' && (
              <div className="space-y-4">
                <h4 className="text-s font-medium text-muted-foreground mb-3">Standard (350x180px)</h4>
                <div className={`bg-white border border-gray-200 rounded-lg ${dimensions.padding} shadow-sm ${dimensions.containerClass}`}>
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`${dimensions.iconSize} rounded-xl ${styles.gradient} flex items-center justify-center shadow-sm`}>
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="mb-2">
                        <span className={`${dimensions.titleSize} font-bold ${styles.primary}`}>{text.title}</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 leading-tight">{getStructureName(structure)}</h4>
                      <p className="text-s text-gray-600 mt-1">{text.complete}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {(structure?.public_infos?.titlets || []).slice(0, 2).map((strength: any, index: number) => (
                      <span key={index} className={`inline-flex items-center px-2 py-1 rounded-full text-ss font-medium ${styles.accent}`}>
                        ✓ {strength}
                      </span>
                    ))}
                    {(structure?.public_infos?.titlets || []).length > 2 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-ss font-medium bg-gray-100 text-gray-700">
                        +{(structure?.public_infos?.titlets || []).length - 2} more
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-ss text-gray-500">
                    <img src="/travelin-logo.svg" alt="Travelin" className="h-4 w-auto" />
                    <span>{text.poweredBy}</span>
                  </div>
                  {/* <Button size="sm" variant="outline" className={`${styles.border} ${styles.primary}`} onClick={() => onGoToPassport && onGoToPassport(selectedStructureSlug ?? fetchedStructure?.slug)}>
                    <ExternalLink className="w-3 h-3 mr-1" />
                    {text.viewButton}
                  </Button> */}
                  <Button
                    size="sm"
                    variant="outline"
                    className={`${styles.border} ${styles.primary}`}
                    onClick={() => {
                      const resolvedId = (structure as any)?.id || fetchedStructure?.id;
                      const resolvedKey = resolvedId || selectedStructureSlug || fetchedStructure?.slug;
                      onGoToPassport2 && onGoToPassport2(resolvedKey);
                    }}
                  >
                    <ExternalLink className="w-3 h-3 mr-1" />
                    Visualizza Passaporto
                  </Button>
                </div>
              </div>
            </div>
          )}

            {widgetSize === 'detailed' && (
              <div className="space-y-4">
                <h4 className="text-s font-medium text-muted-foreground mb-3">Dettagliato (420x240px)</h4>
                <div className={`bg-white border border-gray-200 rounded-lg ${dimensions.padding} shadow-sm ${dimensions.containerClass}`}>
                  <div className="flex items-start gap-4 mb-5">
                    <div className={`${dimensions.iconSize} rounded-xl ${styles.gradient} flex items-center justify-center shadow-lg`}>
                      <CheckCircle className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="mb-2">
                        <span className={`${dimensions.titleSize} font-bold ${styles.primary}`}>{text.digitalTitle}</span>
                      </div>
                      <h3 className="text-l font-semibold text-gray-900 leading-tight mb-1">{getStructureName(structure)}</h3>
                      <p className="text-s text-gray-600">{structure.structure.accommodation_data.type}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <MapPin className="w-3 h-3 text-gray-400" />
                        <span className="text-ss text-gray-500">{structure.structure.accommodation_data.address.city}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-s text-gray-700 leading-relaxed mb-3">
                      {text.description}
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {(structure.public_infos?.titlets || []).map((strength: any, index: number) => (
                        <span key={index} className={`inline-flex items-center px-3 py-1 rounded-full text-ss font-medium ${styles.accent} border`}>
                          ✓ {strength}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-gray-100 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 text-ss text-gray-500">
                        <img src="/travelin-logo.svg" alt="Travelin" className="h-4 w-auto" />
                        <span>{text.certified}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Button size="sm" className={`flex-1 ${styles.gradient} text-white`} onClick={() => onGoToPassport && onGoToPassport(selectedStructureSlug ?? fetchedStructure?.slug)}>
                        <ExternalLink className="w-4 h-4 mr-2" />
                        {text.viewComplete}
                      </Button>
                      <Button size="sm" variant="outline" className="border-gray-300">
                        <Phone className="w-4 h-4 mr-2" />
                        {text.contact}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Other variants omitted for brevity in extracted component */}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-center w-full lg:w-auto lg:min-w-[220px]">
        <Button
          className="w-auto sm:w-full"
          onClick={onSwitchToShareTab}
          disabled={!onSwitchToShareTab}
        >
        Condividi subito il tuo Accessibility Passport
        </Button>
      </div>
    </div>
  );
}
